import React, { useEffect, useMemo, useState } from "react";
import { FaArchive, FaFolder } from "react-icons/fa";
import api from "../../../Services/api";
import "./ModalSelecionarObjeto.css";

const SERVICE_OPTIONS = [
  { key: "Guarda", label: "Guarda Documental", Icon: FaArchive },
  { key: "Govern", label: "Governança", Icon: FaFolder },
];

const ModalSelecionarObjeto = ({
  open,
  onClose,
  IDCliente,
  onConfirm,
}) => {
  const [loadingServices, setLoadingServices] = useState(false);
  const [processos, setProcessos] = useState({});
  const [serviceKey, setServiceKey] = useState(null);
  const [loadingRoot, setLoadingRoot] = useState(false);
  const [rootDirs, setRootDirs] = useState([]);
  const [nodes, setNodes] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const processoSelecionado = useMemo(
    () => (serviceKey ? processos[serviceKey] : null),
    [processos, serviceKey]
  );

  useEffect(() => {
    if (!open) return;
    setSelectedItem(null);
    setServiceKey(null);
    setRootDirs([]);
    setNodes({});
    setSearchTerm("");

    const carregarProcessos = async () => {
      if (!IDCliente) return;
      try {
        setLoadingServices(true);
        const resultados = await Promise.all(
          SERVICE_OPTIONS.map((opt) =>
            api.get(`/processoClienteTipo/${IDCliente}/${opt.key}`)
          )
        );
        const next = {};
        SERVICE_OPTIONS.forEach((opt, index) => {
          const data = resultados[index]?.data;
          next[opt.key] = Array.isArray(data) ? data[0] : data;
        });
        setProcessos(next);
      } catch (error) {
        console.error("Erro ao carregar processos do cliente", error);
      } finally {
        setLoadingServices(false);
      }
    };

    carregarProcessos();
  }, [open, IDCliente]);

  const carregarDiretorio = async (IDDiretorio, IDProcesso) => {
    if (!IDProcesso) return { diretorios: [], arquivos: [] };
    const [diretoriosRes, arquivosRes] = await Promise.all([
      api.get(`/diretorioByIDPai/${IDDiretorio}/${IDProcesso}`),
      IDDiretorio > 0
        ? api.get(`/arquivoByIDPasta/${IDDiretorio}`)
        : Promise.resolve({ data: { arquivos: [] } }),
    ]);
    return {
      diretorios: diretoriosRes?.data?.diretorios || [],
      arquivos: arquivosRes?.data?.arquivos || [],
    };
  };

  const handleSelectService = async (key) => {
    setServiceKey(key);
    setRootDirs([]);
    setNodes({});
    setSelectedItem(null);

    const processo = processos[key];
    if (!processo?.IDProcesso) return;

    try {
      setLoadingRoot(true);
      const { diretorios } = await carregarDiretorio(0, processo.IDProcesso);
      setRootDirs(diretorios);
      setNodes((prev) => {
        const next = { ...prev };
        diretorios.forEach((dir) => {
          if (!next[dir.IDDiretorio]) {
            next[dir.IDDiretorio] = {
              ...dir,
              loaded: false,
              expanded: false,
              childrenDirs: [],
              childrenFiles: [],
            };
          }
        });
        return next;
      });
    } catch (error) {
      console.error("Erro ao carregar diretórios raiz", error);
    } finally {
      setLoadingRoot(false);
    }
  };

  const toggleDir = async (dir) => {
    const processo = processoSelecionado;
    if (!processo?.IDProcesso) return;

    const node = nodes[dir.IDDiretorio];
    const isLoaded = node?.loaded;
    const isExpanded = node?.expanded;

    if (isLoaded) {
      setNodes((prev) => ({
        ...prev,
        [dir.IDDiretorio]: {
          ...prev[dir.IDDiretorio],
          expanded: !isExpanded,
        },
      }));
      return;
    }

    setNodes((prev) => ({
      ...prev,
      [dir.IDDiretorio]: {
        ...dir,
        ...prev[dir.IDDiretorio],
        loading: true,
        expanded: true,
      },
    }));

    try {
      const { diretorios, arquivos } = await carregarDiretorio(
        dir.IDDiretorio,
        processo.IDProcesso
      );
      setNodes((prev) => {
        const next = { ...prev };
        diretorios.forEach((child) => {
          if (!next[child.IDDiretorio]) {
            next[child.IDDiretorio] = {
              ...child,
              loaded: false,
              expanded: false,
              childrenDirs: [],
              childrenFiles: [],
            };
          }
        });
        next[dir.IDDiretorio] = {
          ...next[dir.IDDiretorio],
          loaded: true,
          loading: false,
          expanded: true,
          childrenDirs: diretorios,
          childrenFiles: arquivos,
        };
        return next;
      });
    } catch (error) {
      console.error("Erro ao carregar conteúdo do diretório", error);
      setNodes((prev) => ({
        ...prev,
        [dir.IDDiretorio]: {
          ...prev[dir.IDDiretorio],
          loading: false,
        },
      }));
    }
  };

  const renderDir = (dir, level = 0) => {
    const node = nodes[dir.IDDiretorio] || dir;
    const expanded = node.expanded;
    const nomeDir = (dir.descricaoExibicao || dir.descricao || "Diretório").toLowerCase();
    const filtro = searchTerm.trim().toLowerCase();
    const matchDir = !filtro || nomeDir.includes(filtro);
    const childrenDirs = node.childrenDirs || [];
    const childrenFiles = node.childrenFiles || [];
    const matchChild = !filtro
      ? true
      : childrenDirs.some((child) =>
          (child.descricaoExibicao || child.descricao || "Diretório")
            .toLowerCase()
            .includes(filtro)
        ) ||
        childrenFiles.some((file) =>
          (file.nome || file.descricao || "Arquivo")
            .toLowerCase()
            .includes(filtro)
        );

    if (filtro && !matchDir && !matchChild) {
      return null;
    }

    return (
      <div className="objtree-node" key={`dir-${dir.IDDiretorio}`}>
        <div className="objtree-row" style={{ paddingLeft: level * 14 }}>
          <button
            type="button"
            className="objtree-toggle"
            onClick={() => toggleDir(dir)}
            aria-label={expanded ? "Recolher" : "Expandir"}
          >
            {expanded ? "▾" : "▸"}
          </button>
          <button
            type="button"
            className={`objtree-item ${
              selectedItem?.tipo === 2 &&
              selectedItem?.IDReferencia === dir.IDDiretorio
                ? "is-selected"
                : ""
            }`}
            onClick={() =>
              setSelectedItem({
                tipo: 2,
                IDReferencia: dir.IDDiretorio,
                label: dir.descricaoExibicao || dir.descricao || "Diretório",
              })
            }
          >
            📁 {dir.descricaoExibicao || dir.descricao || "Diretório"}
          </button>
          {node.loading && <span className="objtree-loading">...</span>}
        </div>
          {expanded && node.loaded && (
          <div className="objtree-children">
            {childrenDirs.map((child) => renderDir(child, level + 1))}
            {childrenFiles
              .filter((file) => {
                if (!filtro) return true;
                return (file.nome || file.descricao || "Arquivo")
                  .toLowerCase()
                  .includes(filtro);
              })
              .map((file) => (
              <div
                key={`file-${file.IDArquivo}`}
                className="objtree-row"
                style={{ paddingLeft: (level + 1) * 14 }}
              >
                <span className="objtree-toggle objtree-toggle--empty" />
                <button
                  type="button"
                  className={`objtree-item objtree-item--file ${
                    selectedItem?.tipo === 1 &&
                    selectedItem?.IDReferencia === file.IDArquivo
                      ? "is-selected"
                      : ""
                  }`}
                  onClick={() =>
                    setSelectedItem({
                      tipo: 1,
                      IDReferencia: file.IDArquivo,
                      label: file.nome || file.descricao || "Arquivo",
                    })
                  }
                >
                  📄 {file.nome || file.descricao || "Arquivo"}
                </button>
              </div>
            ))}
            {!childrenDirs.length && !childrenFiles.length && (
              <div
                className="objtree-row objtree-empty"
                style={{ paddingLeft: (level + 1) * 14 }}
              >
                <span>Nenhum item</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!open) return null;

  return (
    <div className="objmodal-overlay">
      <div className="objmodal">
        <div className="objmodal-header">
          <h3>Selecionar arquivo ou diretório</h3>
          <button type="button" className="objmodal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="objmodal-body">
          <div className="objmodal-services">
            {SERVICE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`objmodal-service-btn ${
                  serviceKey === opt.key ? "is-active" : ""
                }`}
                onClick={() => handleSelectService(opt.key)}
                disabled={loadingServices || !processos[opt.key]?.IDProcesso}
                title={
                  processos[opt.key]?.IDProcesso
                    ? opt.label
                    : "Serviço não encontrado para o cliente"
                }
              >
                <opt.Icon />
                {opt.label}
              </button>
            ))}
          </div>
          {loadingServices && (
            <div className="objmodal-status">Carregando serviços...</div>
          )}
          {!loadingServices && serviceKey && !processoSelecionado?.IDProcesso && (
            <div className="objmodal-status">
              Serviço não disponível para este cliente.
            </div>
          )}
          {serviceKey && processoSelecionado?.IDProcesso && (
            <div className="objtree">
              <div className="objtree-search">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Buscar por nome..."
                />
              </div>
              {loadingRoot && (
                <div className="objmodal-status">Carregando diretórios...</div>
              )}
              {!loadingRoot && !rootDirs.length && (
                <div className="objmodal-status">Nenhum diretório encontrado.</div>
              )}
              {!loadingRoot && rootDirs.map((dir) => renderDir(dir, 0))}
            </div>
          )}
        </div>
        <div className="objmodal-footer">
          <div className="objmodal-selection">
            {selectedItem
              ? `Selecionado: ${selectedItem.label}`
              : "Nenhum item selecionado"}
          </div>
          <div className="objmodal-actions">
            <button type="button" className="objmodal-cancel" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="button"
              className="objmodal-confirm"
              disabled={!selectedItem}
              onClick={() => onConfirm?.(selectedItem)}
            >
              Vincular
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalSelecionarObjeto;
